import * as E from './errors';

export interface IParametersValidationError {
	message: string;
};
declare global {
	interface Function {
		//Special method
		//It's the typescript compiler itself who defines the implementation
		//through ts transformer in typescriptCompilerUtil library.
		//Method not available as js method, only ts method
		validateParameters(...x: unknown[]): IParametersValidationError | null;
	}
}
E.libraryAssert(
	!(Function.prototype.validateParameters),
	'Function.prototype.validateParameters already implemented!',
	{}
);
Function.prototype.validateParameters = function () {
	throw E.user('validateParameters methods only available as typescript method AND is not yet shipped!', {});
};


/**
 * Optimized Function.prototype.bind replacement that deduplicates bound functions
 * for identical binding parameters (thisArg and partial arguments).
 * - Caches bound functions in a tree keyed by argument identity and primitive value.
 * - Reuses an existing bound function when the same binding tuple is requested again.
 * - Uses WeakMap/WeakRef to avoid memory leaks and allow garbage collection.
 * Contract:
 * - The behavior must be indistinguishable from the native bind for all inputs.
 * - No duplicate bound function instances should be created for the same binding tuple.
 **/
export const bindingSymbol = Symbol('function.binding');
if ('bind' in Function.prototype) {
	const cp = Function.prototype.bind;
	const cache: WeakMap<Function, CacheNode> = new WeakMap();
	type CacheNode = {
		primitiveChildren?: Map<unknown, CacheNode>;
		objectChildren?: WeakMap<object, CacheNode>;
		boundRef?: WeakRef<Function>;
	};

	function getChild(node: CacheNode, key: unknown): CacheNode {
		if ((typeof key === 'object' && key !== null) || typeof key === 'function') {
			let o = node.objectChildren;
			if (o === undefined) {
				o = node.objectChildren = new WeakMap<object, CacheNode>();
			}
			const obj = key as object;
			let next = o.get(obj);
			if (next === undefined) {
				next = {};
				o.set(obj, next);
			}
			return next;
		} else {
			let p = node.primitiveChildren;
			if (p === undefined) {
				p = node.primitiveChildren = new Map<unknown, CacheNode>();
			}
			let next = p.get(key);
			if (next === undefined) {
				next = {};
				p.set(key, next);
			}
			return next;
		}
	};

	function optimizedBind(this: Function, ...args: Parameters<Function['bind']>) {
		let root = cache.get(this);
		if (root === undefined) {
			root = {};
			cache.set(this, root);
		}
		let node = getChild(root, args.length);
		for (let i = 0; i < args.length; i++) {
			node = getChild(node, args[i]);
		}
		const existing = node.boundRef?.deref();
		if (existing) {
			return existing;
		}
		const fn: Function & { [bindingSymbol]: Function } = cp.call(this, ...args) as any;
		fn[bindingSymbol] = this;
		node.boundRef = new WeakRef(fn);
		return fn;
	};

	Object.defineProperty(Function.prototype, 'bind', {
		value: optimizedBind,
		writable: true,
		configurable: true,
		enumerable: false
	});
	{
		// Sanity check
		const f = (x: number, y: number) => x + y + 2;
		const f1a = f.bind(null, 1);
		const f1b = f.bind(null, 1);
		const f2 = f.bind(null, 2);
		E.libraryAssert(
			(f1a === f1b) && (f1a !== f2),
			'Function.prototype.bind implementation error!',
			{}
		);
	}
}

/**
 *
 **/
export function promisifyCallA(fn: Function) {
	return (new Promise((resolve, reject) => {
		try {
			fn(resolve);
		} catch (error) {
			reject(error);
		}
	}));
};

export function interval(fn: Function, interval: number) {
	return (new Promise((resolve, reject) => {
		let intervalId: ReturnType<typeof setInterval> | undefined;
		try {
			intervalId = setInterval(() => {
				const result = fn();
				if (result !== undefined) {
					clearInterval(intervalId);
					resolve(result);
				}
			}, interval);
		} catch (error) {
			if (intervalId) {
				clearInterval(intervalId);
			}
			reject(error);
		}
	}));
};

export async function imports(...importsPacks: string[][]) {
	const out = [];
	for (const importsPack of importsPacks) {
		out.push(await Promise.all(
			importsPack.map(async (moduleURL) => await import(/*webpackIgnore: true*/moduleURL))
		));
	}
	return (out);
};

export async function timeout(delay: number) {
	return (await new Promise((resolve, reject) => {
		try {
			setTimeout(resolve, delay);
		} catch (error) {
			reject(error);
		}
	}));
};
